// /src/components/security/PermissionsModal.jsx
import React, {
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
} from "react";
import {
  Modal,
  Button,
  InputGroup,
  FormControl,
  ListGroup,
  Badge,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  ShieldCheck,
  Search,
  Copy,
  CheckCircle2,
  PackageOpen,
  FolderTree,
  LockKeyhole,
  ChevronDown,
  ChevronRight,
  Expand,
  Minimize2,
} from "lucide-react";

/** ---- Helpers de parsing ---- */

// Solo trata como "module.action" si toda la cadena cumple este patrón (sin espacios).
const KEY_PATTERN = /^[a-z0-9_-]+\.[a-z0-9_.-]+$/i;

function stripTrailingPunctuation(s) {
  return String(s || "")
    .trim()
    .replace(/[.\u3002]+$/u, ""); // elimina . y 。 al final
}

function normalizeAction(raw) {
  const a = String(raw || "").toLowerCase();
  if (/(^|\.)(create|add|new)/.test(a) || /\b(crear|agregar|nuevo)\b/.test(a))
    return { key: "create", label: "Crear" };
  if (/(^|\.)(edit|update)/.test(a) || /\b(editar|actualizar)\b/.test(a))
    return { key: "edit", label: "Editar" };
  if (/(^|\.)(delete|remove)/.test(a) || /\b(eliminar|borrar)\b/.test(a))
    return { key: "delete", label: "Eliminar" };
  if (
    /(^|\.)(read|reader|view|get|list)/.test(a) ||
    /\b(ver|listar|consultar)\b/.test(a)
  )
    return { key: "read", label: "Ver" };
  if (/(^|\.)(manage|admin)/.test(a) || /\b(gestionar|administrar)\b/.test(a))
    return { key: "manage", label: "Gestionar" };
  return { key: "other", label: "Otros" };
}

const prettifyModule = (key) => key.replace(/[_\-]+/g, " ").trim();

/** Devuelve { moduleKey, moduleLabel, actionKey, actionLabel, original } */
function parsePermission(perm) {
  const original = String(perm ?? "");
  const cleaned = stripTrailingPunctuation(original);
  const lower = cleaned.toLowerCase();

  // 1) Caso clave canónica module.action (sin espacios)
  if (KEY_PATTERN.test(lower)) {
    const [moduleKeyRaw, actionRaw] = lower.split(".", 2);
    const moduleKey = moduleKeyRaw; // ya es minúscula
    const { key: actionKey, label: actionLabel } = normalizeAction(actionRaw);
    return {
      moduleKey,
      moduleLabel: prettifyModule(moduleKey),
      actionKey,
      actionLabel,
      original: cleaned,
    };
  }

  // 2) Texto libre → inferir módulo por palabras clave
  let moduleKey = "otros";
  if (/\babogados?\b/.test(lower)) moduleKey = "abogados";
  else if (/\bsecurity[_\s-]*users?\b|\busuarios?\b/.test(lower))
    moduleKey = "security_users";
  else if (/\bsecurity[_\s-]*roles?\b|\broles?\b/.test(lower))
    moduleKey = "security_roles";
  else if (/\bcompany|empresa\b/.test(lower)) moduleKey = "company";
  else if (/\blicen[cs]ia|license\b/.test(lower)) moduleKey = "license";
  else if (/\bdashboard\b/.test(lower)) moduleKey = "dashboard";

  const { key: actionKey, label: actionLabel } = normalizeAction(lower);

  return {
    moduleKey,
    moduleLabel: prettifyModule(moduleKey),
    actionKey,
    actionLabel,
    original: cleaned,
  };
}

/** ---- Componente principal ---- */

const PermissionsModal = ({ show, onHide, permissions = [], roleName }) => {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const searchRef = useRef(null);

  useEffect(() => {
    const id = setTimeout(
      () => setDebouncedQuery(query.trim().toLowerCase()),
      200
    );
    return () => clearTimeout(id);
  }, [query]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const filtered = useMemo(() => {
    if (!debouncedQuery) return permissions;
    return permissions
      .map(stripTrailingPunctuation)
      .filter((p) => String(p).toLowerCase().includes(debouncedQuery));
  }, [permissions, debouncedQuery]);

  // Agrupar por módulo → acción
  const grouped = useMemo(() => {
    const modules = new Map(); // moduleKey -> { label, actions: Map }
    filtered.forEach((perm) => {
      const p = parsePermission(perm);
      if (!modules.has(p.moduleKey)) {
        modules.set(p.moduleKey, { label: p.moduleLabel, actions: new Map() });
      }
      const mod = modules.get(p.moduleKey);
      if (!mod.actions.has(p.actionKey)) {
        mod.actions.set(p.actionKey, { label: p.actionLabel, items: [] });
      }
      mod.actions.get(p.actionKey).items.push(p.original);
    });

    const actionOrder = ["create", "edit", "delete", "read", "manage", "other"];

    return Array.from(modules.entries())
      .sort(([a], [b]) =>
        a.localeCompare(b, undefined, { sensitivity: "base" })
      )
      .map(([moduleKey, mod]) => {
        const actions = Array.from(mod.actions.entries())
          .sort(([a], [b]) => actionOrder.indexOf(a) - actionOrder.indexOf(b))
          .map(([key, info]) => ({
            key,
            label: info.label,
            items: info.items.sort((x, y) =>
              x.localeCompare(y, undefined, { sensitivity: "base" })
            ),
          }));
        const total = actions.reduce((acc, a) => acc + a.items.length, 0);
        return { moduleKey, moduleLabel: mod.label, actions, total };
      });
  }, [filtered]);

  useEffect(() => {
    if (show) {
      setExpanded(new Set(grouped.map((g) => g.moduleKey)));
      setCopied(false);
    }
  }, [show, grouped]);

  const iconForModule = (moduleLabel) => {
    const key = (moduleLabel || "").toLowerCase();
    if (key.includes("company") || key.includes("empresa"))
      return <FolderTree size={18} className="me-2" />;
    if (key.includes("user") || key.includes("usuario"))
      return <LockKeyhole size={18} className="me-2" />;
    if (key.includes("license") || key.includes("licencia"))
      return <PackageOpen size={18} className="me-2" />;
    return <ShieldCheck size={18} className="me-2" />;
  };

  const handleCopy = async () => {
    const text = filtered.join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {}
  };

  const toggleModule = useCallback((moduleKey) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(moduleKey) ? next.delete(moduleKey) : next.add(moduleKey);
      return next;
    });
  }, []);

  const expandAll = () => setExpanded(new Set(grouped.map((g) => g.moduleKey)));
  const collapseAll = () => setExpanded(new Set());

  const empty = permissions.length === 0;
  const emptyFiltered = !empty && filtered.length === 0;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      aria-labelledby="perms-title"
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <div className="d-flex align-items-center w-100">
          <div
            className="d-flex align-items-center justify-content-center rounded-circle me-3"
            style={{
              width: 44,
              height: 44,
              background:
                "linear-gradient(135deg, rgba(102,16,242,0.15), rgba(13,110,253,0.15))",
            }}
            aria-hidden
          >
            <ShieldCheck size={22} />
          </div>
          <div className="flex-grow-1">
            <Modal.Title id="perms-title" className="mb-0">
              Permisos del rol{roleName ? `: ${roleName}` : ""}
            </Modal.Title>
            <div className="text-muted small">
              {permissions.length} total · {filtered.length} visibles
              {debouncedQuery ? ` · filtro: “${query}”` : ""}
            </div>
          </div>

          {!empty && (
            <div className="d-flex gap-2">
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip>
                    {copied ? "¡Copiado!" : "Copiar lista filtrada"}
                  </Tooltip>
                }
              >
                <Button
                  variant={copied ? "success" : "outline-secondary"}
                  size="sm"
                  onClick={handleCopy}
                >
                  {copied ? (
                    <CheckCircle2 size={16} className="me-1" />
                  ) : (
                    <Copy size={16} className="me-1" />
                  )}
                  {copied ? "Copiado" : "Copiar"}
                </Button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip>Expandir todo</Tooltip>}
              >
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={expandAll}
                >
                  <Expand size={16} />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip>Colapsar todo</Tooltip>}
              >
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={collapseAll}
                >
                  <Minimize2 size={16} />
                </Button>
              </OverlayTrigger>
            </div>
          )}
        </div>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: "65vh", overflowY: "auto" }}>
        {!empty && (
          <InputGroup
            className="mb-3"
            role="search"
            aria-label="Buscar permisos"
          >
            <InputGroup.Text>
              <Search size={16} />
            </InputGroup.Text>
            <FormControl
              ref={searchRef}
              placeholder='Buscar permiso (p. ej., "abogados.view" o "Permite editar...")'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Buscar permiso"
            />
          </InputGroup>
        )}

        {empty ? (
          <EmptyBlock
            icon={<PackageOpen size={42} className="mb-2 text-muted" />}
            title="Sin permisos configurados."
            subtitle="Agrega permisos a este rol para verlos aquí."
          />
        ) : emptyFiltered ? (
          <EmptyBlock
            icon={<Search size={38} className="mb-2 text-muted" />}
            title="No hay permisos que coincidan con tu búsqueda."
            subtitle="Intenta con otro término."
          />
        ) : (
          <div className="d-flex flex-column gap-3">
            {grouped.map((g) => (
              <ModuleGroup
                key={g.moduleKey}
                module={g.moduleLabel}
                total={g.total}
                actions={g.actions}
                icon={iconForModule(g.moduleLabel)}
                expanded={expanded.has(g.moduleKey)}
                onToggle={() => toggleModule(g.moduleKey)}
                highlight={debouncedQuery}
              />
            ))}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <Button variant="secondary" onClick={onHide}>
          Cerrar
        </Button>
        {!empty && (
          <Badge bg="light" text="dark">
            <CheckCircle2 size={14} className="me-1" />
            {filtered.length} visibles
          </Badge>
        )}
      </Modal.Footer>
    </Modal>
  );
};

/** ---- Subcomponentes ---- */

const EmptyBlock = ({ icon, title, subtitle }) => (
  <div className="text-center py-4">
    {icon}
    <p className="text-muted mb-1">{title}</p>
    <small className="text-muted">{subtitle}</small>
  </div>
);

const ModuleGroup = ({
  module,
  total,
  actions,
  icon,
  expanded,
  onToggle,
  highlight,
}) => (
  <div className="border rounded-3">
    <button
      type="button"
      className="w-100 d-flex align-items-center justify-content-between px-3 py-2 border-bottom bg-light text-start"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-controls={`group-${module}`}
      style={{ cursor: "pointer" }}
    >
      <div className="d-flex align-items-center text-dark">
        {icon}
        <strong className="me-2 text-dark">{module}</strong>
        <Badge bg="secondary" pill>
          {total}
        </Badge>
      </div>
      {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
    </button>

    {expanded && (
      <div id={`group-${module}`} className="p-2">
        {actions.map((a) => (
          <ActionSection
            key={a.key}
            label={a.label}
            items={a.items}
            highlight={highlight}
          />
        ))}
      </div>
    )}
  </div>
);

const ActionSection = ({ label, items, highlight }) => {
  if (!items?.length) return null;
  return (
    <div className="mb-2">
      <div className="d-flex align-items-center mb-1">
        <span className="fw-semibold me-2">{label}</span>
        <Badge bg="light" text="dark">
          {items.length}
        </Badge>
      </div>
      <ListGroup variant="flush">
        {items.map((perm) => (
          <ListGroup.Item key={perm} className="d-flex align-items-center py-1">
            <CheckCircle2 size={14} className="me-2 text-success" />
            <code className="mb-0">{renderWithHighlight(perm, highlight)}</code>
          </ListGroup.Item>
        ))}
      </ListGroup>
    </div>
  );
};

function renderWithHighlight(text, query) {
  if (!query) return text;
  const lower = String(text).toLowerCase();
  const idx = lower.indexOf(query);
  if (idx === -1) return text;
  const before = String(text).slice(0, idx);
  const match = String(text).slice(idx, idx + query.length);
  const after = String(text).slice(idx + query.length);
  return (
    <>
      {before}
      <mark>{match}</mark>
      {after}
    </>
  );
}

export default PermissionsModal;
